/**
 * UploadAssetsModal - Modal for uploading and tagging assets before adding to library
 */

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Upload, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAssets } from "@/hooks/useAssets";
import { useScenes } from "@/hooks/shots/useScenes";
import { toast } from "sonner";
import { generateUUID } from "@/utils/uuid";
import { AssetUploadCard } from "./AssetUploadCard";
import { SelectAllBar } from "./SelectAllBar";
import {
  type UploadingAsset,
  type AssetTags,
  detectAssetType,
  generateMockTitle,
  generateMockDescription,
  createDefaultTags,
  isSupportedAssetType,
} from "./types";
import "./UploadAssetsModal.css";

interface UploadAssetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  /** Pre-loaded files from global drop */
  initialFiles?: File[];
}

export function UploadAssetsModal({
  isOpen,
  onClose,
  projectId,
  initialFiles = [],
}: UploadAssetsModalProps) {
  const { uploadAsset, refetchAssets } = useAssets(projectId, {
    fetchEnabled: isOpen,
  });
  const { data: scenes = [] } = useScenes(projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Assets in upload queue
  const [uploadingAssets, setUploadingAssets] = useState<UploadingAsset[]>([]);

  // Selected asset IDs for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Whether we're currently uploading
  const [isUploading, setIsUploading] = useState(false);

  // Track if initial files have been processed
  const initialFilesProcessedRef = useRef(false);

  // Create thumbnail for visual assets
  const createThumbnail = useCallback(
    (file: File): Promise<string | undefined> => {
      return new Promise((resolve) => {
        if (
          !file.type.startsWith("image/") &&
          !file.type.startsWith("video/")
        ) {
          resolve(undefined);
          return;
        }

        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result as string);
          };
          reader.onerror = () => resolve(undefined);
          reader.readAsDataURL(file);
        } else if (file.type.startsWith("video/")) {
          // Create video thumbnail from first frame
          const video = document.createElement("video");
          video.preload = "metadata";
          video.muted = true;

          video.onloadeddata = () => {
            video.currentTime = 0;
          };

          video.onseeked = () => {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.drawImage(video, 0, 0);
              resolve(canvas.toDataURL("image/jpeg"));
            } else {
              resolve(undefined);
            }
            URL.revokeObjectURL(video.src);
          };

          video.onerror = () => {
            resolve(undefined);
            URL.revokeObjectURL(video.src);
          };

          video.src = URL.createObjectURL(file);
        }
      });
    },
    [],
  );

  // Add files to the upload queue
  const addFiles = useCallback(
    async (files: File[]) => {
      const supportedFiles = files.filter(isSupportedAssetType);

      if (supportedFiles.length < files.length) {
        const unsupportedCount = files.length - supportedFiles.length;
        toast.warning(
          `${unsupportedCount} file${unsupportedCount > 1 ? "s" : ""} skipped - unsupported format`,
        );
      }

      const newAssets: UploadingAsset[] = await Promise.all(
        supportedFiles.map(async (file) => {
          const type = detectAssetType(file);
          const thumbnail = await createThumbnail(file);

          return {
            id: generateUUID(),
            file,
            status: "pending" as const,
            type,
            thumbnail,
            title: generateMockTitle(file),
            description: generateMockDescription(file),
            tags: createDefaultTags(type),
          };
        }),
      );

      setUploadingAssets((prev) => [...prev, ...newAssets]);
    },
    [createThumbnail],
  );

  // Process initial files when modal opens
  useEffect(() => {
    if (
      isOpen &&
      initialFiles.length > 0 &&
      !initialFilesProcessedRef.current
    ) {
      initialFilesProcessedRef.current = true;
      addFiles(initialFiles);
    }
    // Reset the ref when modal closes
    if (!isOpen) {
      initialFilesProcessedRef.current = false;
    }
  }, [isOpen, initialFiles, addFiles]);

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0) {
        addFiles(files);
      }
      // Reset input so same files can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [addFiles],
  );

  // Handle drop zone drag events
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        addFiles(files);
      }
    },
    [addFiles],
  );

  // Update asset title
  const handleTitleChange = useCallback((id: string, title: string) => {
    setUploadingAssets((prev) =>
      prev.map((asset) => (asset.id === id ? { ...asset, title } : asset)),
    );
  }, []);

  // Update asset tags
  const handleTagsChange = useCallback((id: string, tags: AssetTags) => {
    setUploadingAssets((prev) =>
      prev.map((asset) => (asset.id === id ? { ...asset, tags } : asset)),
    );
  }, []);

  // Remove an asset from the queue
  const handleRemoveAsset = useCallback((id: string) => {
    setUploadingAssets((prev) => prev.filter((asset) => asset.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Toggle selection of an asset
  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    let ready = 0;
    let processing = 0;
    uploadingAssets.forEach((asset) => {
      if (asset.status === "pending" || asset.status === "complete") {
        ready++;
      } else if (asset.status === "uploading") {
        processing++;
      }
    });
    return { ready, processing };
  }, [uploadingAssets]);

  const isAllSelected =
    selectedIds.size === uploadingAssets.length && uploadingAssets.length > 0;

  // Handle select all toggle
  const handleSelectAllToggle = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(uploadingAssets.map((a) => a.id)));
    }
  }, [isAllSelected, uploadingAssets]);

  // Add category to selected items
  const handleAddCategoryToSelected = useCallback(
    (category: string) => {
      setUploadingAssets((prev) =>
        prev.map((asset) => {
          if (!selectedIds.has(asset.id)) return asset;
          if (asset.tags.category.includes(category)) return asset;
          return {
            ...asset,
            tags: {
              ...asset.tags,
              category: [...asset.tags.category, category],
            },
          };
        }),
      );
    },
    [selectedIds],
  );

  // Set scene on selected items
  const handleSetSceneToSelected = useCallback(
    (sceneId: string) => {
      setUploadingAssets((prev) =>
        prev.map((asset) => {
          if (!selectedIds.has(asset.id)) return asset;
          return {
            ...asset,
            tags: {
              ...asset.tags,
              scene: sceneId,
            },
          };
        }),
      );
    },
    [selectedIds],
  );

  // Upload all assets
  const handleUploadAll = useCallback(async () => {
    if (uploadingAssets.length === 0) return;

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Upload each asset
      for (const asset of uploadingAssets) {
        // Update status to uploading
        setUploadingAssets((prev) =>
          prev.map((a) =>
            a.id === asset.id ? { ...a, status: "uploading" as const } : a,
          ),
        );

        try {
          await uploadAsset(asset.file);
          successCount++;

          // Update status to complete
          setUploadingAssets((prev) =>
            prev.map((a) =>
              a.id === asset.id ? { ...a, status: "complete" as const } : a,
            ),
          );
        } catch (error) {
          errorCount++;
          // Update status to error
          setUploadingAssets((prev) =>
            prev.map((a) =>
              a.id === asset.id
                ? {
                    ...a,
                    status: "error" as const,
                    error:
                      error instanceof Error ? error.message : "Upload failed",
                  }
                : a,
            ),
          );
        }
      }

      // Handle upload completion based on actual results
      if (errorCount === 0) {
        toast.success(
          `${successCount} asset${successCount > 1 ? "s" : ""} added to library`,
        );
        await refetchAssets();
        onClose();
      } else if (successCount > 0) {
        toast.warning(`${successCount} uploaded, ${errorCount} failed`);
        await refetchAssets();
      } else {
        toast.error("All uploads failed");
      }
    } finally {
      setIsUploading(false);
    }
  }, [uploadingAssets, uploadAsset, refetchAssets, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setUploadingAssets([]);
    setSelectedIds(new Set());
    onClose();
  }, [onClose]);

  // Reset state when modal closes
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setUploadingAssets([]);
        setSelectedIds(new Set());
        onClose();
      }
    },
    [onClose],
  );

  const hasAssets = uploadingAssets.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="upload-panel"
        className="upload-assets-modal"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="upload-assets-modal__header">
          <div className="flex items-center justify-between">
            <DialogTitle>Upload Assets</DialogTitle>
            <button
              type="button"
              onClick={handleCancel}
              aria-label="Close modal"
              className="upload-assets-modal__close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="upload-assets-modal__content">
          {/* Select All Bar - shown when there are assets */}
          {hasAssets && (
            <SelectAllBar
              totalCount={uploadingAssets.length}
              selectedCount={selectedIds.size}
              readyCount={statusCounts.ready}
              processingCount={statusCounts.processing}
              isAllSelected={isAllSelected}
              scenes={scenes}
              onSelectAllToggle={handleSelectAllToggle}
              onAddCategory={handleAddCategoryToSelected}
              onSetScene={handleSetSceneToSelected}
              onAddMore={() => fileInputRef.current?.click()}
            />
          )}

          {/* Drop Zone / Asset Grid */}
          {hasAssets ? (
            <div
              data-testid="upload-list"
              className="upload-assets-modal__grid"
            >
              {uploadingAssets.map((asset) => (
                <AssetUploadCard
                  key={asset.id}
                  asset={asset}
                  scenes={scenes}
                  isSelected={selectedIds.has(asset.id)}
                  onTitleChange={(title) => handleTitleChange(asset.id, title)}
                  onTagsChange={(tags) => handleTagsChange(asset.id, tags)}
                  onRemove={() => handleRemoveAsset(asset.id)}
                  onToggleSelect={() => handleToggleSelect(asset.id)}
                />
              ))}
            </div>
          ) : (
            <div
              data-testid="upload-dropzone"
              className={cn(
                "upload-assets-modal__dropzone",
                isDragging && "upload-assets-modal__dropzone--dragging",
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="upload-assets-modal__dropzone-icon">
                <Upload className="w-6 h-6" />
              </div>
              <p className="upload-assets-modal__dropzone-title">
                {isDragging
                  ? "Drop files here"
                  : "Drop files here or click to browse"}
              </p>
              <p className="upload-assets-modal__dropzone-subtitle">
                Images, videos, and audio files
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,audio/*"
            multiple
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>

        {/* Footer */}
        <DialogFooter className="upload-assets-modal__footer">
          <div className="upload-assets-modal__footer-right">
            <Button
              data-testid="upload-cancel-button"
              variant="utility"
              size="sm"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              data-testid="upload-submit-button"
              variant="pink"
              size="sm"
              onClick={handleUploadAll}
              disabled={!hasAssets || isUploading}
            >
              {isUploading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add to Library
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
